
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/lib/supabase";
import { useToast } from "@/hooks/use-toast";
import { KarmaAvailablePoints } from "../types/karma-types";

export const useKarmaValidation = (
  setAvailablePoints: React.Dispatch<React.SetStateAction<KarmaAvailablePoints>>
) => {
  const { user } = useAuth();
  const { toast } = useToast();

  const canClaimPoints = async (type: 'daily' | 'weekly'): Promise<boolean> => {
    if (!user) {
      toast({
        title: "Error",
        description: "You must be logged in to claim points",
        variant: "destructive"
      });
      return false;
    }

    // Double-check if points can be claimed by validating against the database
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Start of today
    
    const oneWeekAgo = new Date();
    oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);
    oneWeekAgo.setHours(0, 0, 0, 0); // Start of the day 7 days ago
    
    // Query the database directly to check if points were already claimed
    const { data, error } = await supabase
      .from('karma_activities')
      .select('id, created_at')
      .eq('user_id', user.id)
      .eq('activity_type', type === 'daily' ? 'login' : 'activity');
      
    if (error) {
      console.error("Error verifying claim status:", error);
      // Do not toast here, let the main claim function handle generic errors
      // if the check itself fails. This is about claim *eligibility*.
      // Only toast if ALREADY claimed.
      return true; // Assume eligible if check fails, main function will catch DB error.
    }
    
    if (data && data.length > 0) {
      const recentClaims = data.filter(claim => {
        const claimDate = new Date(claim.created_at);
        
        if (type === 'daily') {
          claimDate.setHours(0, 0, 0, 0); 
          return claimDate.getTime() === today.getTime();
        } else { // weekly
          return claimDate >= oneWeekAgo;
        }
      });
      
      if (recentClaims.length > 0) {
        toast({
          title: "Already Claimed",
          description: `You have already claimed your ${type} points for this period`,
          variant: "default"
        });
        setAvailablePoints(prev => ({ ...prev, [type]: false }));
        return false;
      }
    }
    return true; // Points can be claimed
  };

  return { canClaimPoints };
};

